/**
 * useMultiExchangeOrderbook Hook
 *
 * Manages CCXT Pro WebSocket subscriptions for spot exchange orderbooks.
 * Uses real-time WebSocket streams for instant orderbook updates.
 *
 * Exchanges:
 * - Binance Spot: watchOrderBook() WebSocket (USDT pairs)
 * - Upbit: watchOrderBook() WebSocket (KRW pairs)
 * - Bithumb: watchOrderBook() WebSocket (KRW pairs)
 *
 * Note: Using CCXT Pro (now free) for real-time WebSocket updates.
 * Provides instant orderbook updates as they happen on the exchange.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Exchange as CcxtExchange } from 'ccxt';
import type { OrderbookResponse } from '../types/orderbook';

// Dynamic CCXT import (browser-compatible)
type CcxtModule = typeof import('ccxt');
type CcxtModuleWithPro = CcxtModule & { pro?: Record<string, unknown> };
let ccxtModulePromise: Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> | null = null;

async function loadCcxtModule(): Promise<{ module: CcxtModuleWithPro; usingPro: boolean }> {
  console.log('[useMultiExchangeOrderbook] loadCcxtModule called');
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      console.log('[useMultiExchangeOrderbook] CCXT import successful!', { hasDefault: !!raw.default, keys: Object.keys(raw).slice(0, 5) });
      const resolved = (raw.default ?? raw) as CcxtModuleWithPro;
      const hasProNamespace = resolved.pro && typeof resolved.pro === 'object';
      console.log('[useMultiExchangeOrderbook] CCXT Pro namespace check:', { hasProNamespace, proKeys: hasProNamespace ? Object.keys(resolved.pro!).slice(0, 5) : [] });
      return {
        module: resolved,
        usingPro: Boolean(hasProNamespace),
      };
    }).catch((error) => {
      console.error('[useMultiExchangeOrderbook] CCXT import FAILED:', error);
      throw error;
    });
  }
  return ccxtModulePromise;
}

export interface ExchangeOrderbookState {
  orderbook: OrderbookResponse | null;
  loading: boolean;
  error: string | null;
  lastUpdate: number;
}

export interface MultiExchangeOrderbookState {
  upbit: ExchangeOrderbookState;
  bithumb: ExchangeOrderbookState;
  binance: ExchangeOrderbookState;
  isAnyLoading: boolean;
  hasAnyData: boolean;
}

interface UseMultiExchangeOrderbookOptions {
  symbol: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
  enabled?: boolean;
}

const initialExchangeState: ExchangeOrderbookState = {
  orderbook: null,
  loading: false,
  error: null,
  lastUpdate: 0,
};

/**
 * Convert CCXT orderbook format to our OrderbookResponse format
 */
function convertCcxtOrderbook(ccxtOrderbook: any, symbol: string): OrderbookResponse {
  return {
    exchange: '', // Will be filled by caller
    symbol,
    bids: ccxtOrderbook.bids.map((level: [number, number]) => ({
      price: level[0],
      volume: level[1],
    })),
    asks: ccxtOrderbook.asks.map((level: [number, number]) => ({
      price: level[0],
      volume: level[1],
    })),
    timestamp: ccxtOrderbook.timestamp || Date.now(),
  };
}

/**
 * Fetch orderbooks from multiple exchanges for a given symbol using CCXT WebSocket subscriptions
 *
 * @param options.symbol - The trading pair symbol (e.g., "BTCUSDT", "ETHUSDT")
 * @param options.enabled - Enable the hook (default: true)
 */
export function useMultiExchangeOrderbook({
  symbol,
  enabled = true,
}: UseMultiExchangeOrderbookOptions): MultiExchangeOrderbookState {
  const [upbitState, setUpbitState] = useState<ExchangeOrderbookState>(initialExchangeState);
  const [bithumbState, setBithumbState] = useState<ExchangeOrderbookState>(initialExchangeState);
  const [binanceState, setBinanceState] = useState<ExchangeOrderbookState>(initialExchangeState);

  // Exchange client refs (stable across renders)
  const binanceSpotRef = useRef<CcxtExchange | null>(null);
  const upbitRef = useRef<CcxtExchange | null>(null);
  const bithumbRef = useRef<CcxtExchange | null>(null);

  // CCXT module loaded state
  const [ccxtLoaded, setCcxtLoaded] = useState(false);

  // Active WebSocket connection flags
  const connectionsRef = useRef({
    binanceSpot: false,
    upbit: false,
    bithumb: false,
  });

  /**
   * Subscribe to Binance Spot orderbook using WebSocket
   */
  const subscribeBinanceSpot = useCallback(
    async (ccxtSymbol: string) => {
      if (!binanceSpotRef.current || !enabled) return;

      console.info(`[useMultiExchangeOrderbook] Starting Binance Spot orderbook WebSocket: ${ccxtSymbol}`);

      try {
        setBinanceState((prev) => ({ ...prev, loading: true, error: null }));

        while (enabled && connectionsRef.current.binanceSpot && binanceSpotRef.current) {
          const orderbook = await binanceSpotRef.current.watchOrderBook(ccxtSymbol);

          // Convert to our format
          const converted = convertCcxtOrderbook(orderbook, symbol);
          converted.exchange = 'binance';

          setBinanceState({
            orderbook: converted,
            loading: false,
            error: null,
            lastUpdate: Date.now(),
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useMultiExchangeOrderbook] Binance Spot WebSocket error:', errorMessage);
        setBinanceState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    },
    [enabled, symbol]
  );

  /**
   * Subscribe to Upbit orderbook using WebSocket
   */
  const subscribeUpbit = useCallback(
    async (ccxtSymbol: string) => {
      if (!upbitRef.current || !enabled) return;

      console.info(`[useMultiExchangeOrderbook] Starting Upbit orderbook WebSocket: ${ccxtSymbol}`);

      try {
        setUpbitState((prev) => ({ ...prev, loading: true, error: null }));

        while (enabled && connectionsRef.current.upbit && upbitRef.current) {
          const orderbook = await upbitRef.current.watchOrderBook(ccxtSymbol);

          // Convert to our format
          const converted = convertCcxtOrderbook(orderbook, symbol);
          converted.exchange = 'upbit';

          setUpbitState({
            orderbook: converted,
            loading: false,
            error: null,
            lastUpdate: Date.now(),
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useMultiExchangeOrderbook] Upbit WebSocket error:', errorMessage);
        setUpbitState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    },
    [enabled, symbol]
  );

  /**
   * Subscribe to Bithumb orderbook using WebSocket
   */
  const subscribeBithumb = useCallback(
    async (ccxtSymbol: string) => {
      if (!bithumbRef.current || !enabled) return;

      console.info(`[useMultiExchangeOrderbook] Starting Bithumb orderbook WebSocket: ${ccxtSymbol}`);

      try {
        setBithumbState((prev) => ({ ...prev, loading: true, error: null }));

        while (enabled && connectionsRef.current.bithumb && bithumbRef.current) {
          const orderbook = await bithumbRef.current.watchOrderBook(ccxtSymbol);

          // Convert to our format
          const converted = convertCcxtOrderbook(orderbook, symbol);
          converted.exchange = 'bithumb';

          setBithumbState({
            orderbook: converted,
            loading: false,
            error: null,
            lastUpdate: Date.now(),
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('[useMultiExchangeOrderbook] Bithumb WebSocket error:', errorMessage);
        setBithumbState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }));
      }
    },
    [enabled, symbol]
  );

  /**
   * Load CCXT module and initialize exchange clients on mount
   */
  useEffect(() => {
    if (!enabled) return;

    // Skip if already initialized (prevent React Strict Mode double-initialization)
    if (ccxtLoaded && binanceSpotRef.current && upbitRef.current && bithumbRef.current) {
      console.log('[useMultiExchangeOrderbook] CCXT clients already initialized, skipping');
      return;
    }

    let mounted = true;

    // Load CCXT dynamically
    loadCcxtModule().then(({ module: ccxtModule, usingPro }) => {
      if (!mounted) return;

      if (!usingPro) {
        console.warn('[useMultiExchangeOrderbook] CCXT Pro namespace not available - WebSocket subscriptions will not work. Using regular CCXT.');
        return;
      }

      console.info('[useMultiExchangeOrderbook] Initializing spot exchange clients (CCXT Pro WebSocket)...');

      // Type assertion for pro namespace access
      type ExchangeConstructor = new (params?: Record<string, unknown>) => CcxtExchange;
      const proBinance = (ccxtModule.pro as Record<string, ExchangeConstructor>)['binance'];
      const proUpbit = (ccxtModule.pro as Record<string, ExchangeConstructor>)['upbit'];
      const proBithumb = (ccxtModule.pro as Record<string, ExchangeConstructor>)['bithumb'];

      // Initialize CCXT Pro clients for WebSocket access
      binanceSpotRef.current = new proBinance({
        enableRateLimit: true,
        options: { defaultType: 'spot' },
      });

      upbitRef.current = new proUpbit({
        enableRateLimit: true,
      });

      bithumbRef.current = new proBithumb({
        enableRateLimit: true,
      });

      console.info('[useMultiExchangeOrderbook] Spot exchange clients initialized');
      setCcxtLoaded(true);
    }).catch((error) => {
      console.error('[useMultiExchangeOrderbook] Failed to load CCXT module:', error);
    });

    // Cleanup on unmount
    return () => {
      mounted = false;
      console.info('[useMultiExchangeOrderbook] Cleanup function called (React Strict Mode or actual unmount)');

      // Stop all connections
      connectionsRef.current.binanceSpot = false;
      connectionsRef.current.upbit = false;
      connectionsRef.current.bithumb = false;
    };
  }, [enabled, ccxtLoaded]);

  /**
   * Start WebSocket subscriptions when symbol changes
   */
  useEffect(() => {
    if (!enabled || !ccxtLoaded || !symbol) return;

    // Convert symbol to CCXT format for each exchange
    // Binance: BTCUSDT → BTC/USDT
    // Upbit: BTCUSDT → BTC/KRW
    // Bithumb: BTCUSDT → BTC/KRW
    const base = symbol.replace(/USDT$/, '').toUpperCase();
    const binanceSymbol = `${base}/USDT`;
    const krwSymbol = `${base}/KRW`;

    console.info(`[useMultiExchangeOrderbook] Starting WebSocket subscriptions for symbol: ${symbol}`, {
      binance: binanceSymbol,
      upbit: krwSymbol,
      bithumb: krwSymbol,
    });

    // Start WebSocket subscriptions for each exchange
    if (!connectionsRef.current.binanceSpot) {
      connectionsRef.current.binanceSpot = true;
      subscribeBinanceSpot(binanceSymbol);
    }

    if (!connectionsRef.current.upbit) {
      connectionsRef.current.upbit = true;
      subscribeUpbit(krwSymbol);
    }

    if (!connectionsRef.current.bithumb) {
      connectionsRef.current.bithumb = true;
      subscribeBithumb(krwSymbol);
    }

    // Cleanup function to stop WebSocket connections
    return () => {
      connectionsRef.current.binanceSpot = false;
      connectionsRef.current.upbit = false;
      connectionsRef.current.bithumb = false;
    };
  }, [enabled, ccxtLoaded, symbol, subscribeBinanceSpot, subscribeUpbit, subscribeBithumb]);

  const isAnyLoading = upbitState.loading || bithumbState.loading || binanceState.loading;
  const hasAnyData = !!(upbitState.orderbook || bithumbState.orderbook || binanceState.orderbook);

  return {
    upbit: upbitState,
    bithumb: bithumbState,
    binance: binanceState,
    isAnyLoading,
    hasAnyData,
  };
}
